import Service from '@ember/service';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type SessionService from 'tio-employee/services/session';

/**
 * Tracks whether the current session is an admin-impersonated session.
 *
 * Delegates to the session service which decodes the JWT `impersonated_by`
 * claim. Provides the mutation-confirmation dialog state for the adapter.
 */
export default class ImpersonationService extends Service {
  @service declare session: SessionService;

  /**
   * Whether a mutation confirmation dialog is currently showing.
   * The adapter sets this to `true` and waits for the user's choice.
   */
  @tracked showMutationConfirm = false;

  /** Resolver for the in-flight mutation confirmation promise. */
  private _resolveMutationConfirm?: (confirmed: boolean) => void;

  /** In-flight confirmation promise (guards against re-entry). */
  private _pendingConfirmation?: Promise<boolean>;

  get isImpersonated() {
    return this.session.isImpersonated;
  }

  get impersonatorPersonId() {
    return this.session.impersonatedByPersonId;
  }

  /**
   * Shows a confirmation dialog and returns a promise that resolves when the
   * user confirms or cancels. Used by the adapter before mutating requests.
   */
  confirmMutation() {
    if (this._pendingConfirmation) {
      return this._pendingConfirmation;
    }
    this.showMutationConfirm = true;
    this._pendingConfirmation = new Promise<boolean>((resolve) => {
      this._resolveMutationConfirm = resolve;
    }).finally(() => {
      this._pendingConfirmation = undefined;
    });
    return this._pendingConfirmation;
  }

  /** Called from the dialog when the user confirms. */
  handleConfirm = () => {
    this.showMutationConfirm = false;
    this._resolveMutationConfirm?.(true);
    this._resolveMutationConfirm = undefined;
  };

  /** Called from the dialog when the user cancels. */
  handleCancel = () => {
    this.showMutationConfirm = false;
    this._resolveMutationConfirm?.(false);
    this._resolveMutationConfirm = undefined;
  };
}

declare module '@ember/service' {
  interface Registry {
    impersonation: ImpersonationService;
  }
}
